// Keyboard navigation of the Variables table (spec 6.10.16): one focused cell, moved by the grid keys.
// Group rows have a single cell, so their column clamps to 0 and the column to return to is remembered.

export interface GridPosition {
  row: number;
  col: number;
}

export interface GridShape {
  rows: number;
  /** Cells in a row (1 for group rows). */
  cellsIn(row: number): number;
  /** Rows per PageUp/PageDown. */
  page?: number;
}

export interface GridKey {
  key: string;
  ctrlKey?: boolean;
  metaKey?: boolean;
}

/**
 * The position after a navigation key, or null when the key doesn't navigate. `wanted` is the column the
 * user last chose, kept while passing through group rows.
 */
export function moveInGrid(
  position: GridPosition,
  event: GridKey,
  shape: GridShape,
  wanted = position.col,
): { position: GridPosition; wanted: number } | null {
  if (shape.rows === 0) return null;
  const ctrl = !!(event.ctrlKey || event.metaKey);
  const page = shape.page ?? 10;
  const last = shape.rows - 1;
  const clampRow = (row: number) => Math.max(0, Math.min(last, row));
  const at = (row: number, col: number) => {
    const r = clampRow(row);
    const cells = Math.max(1, shape.cellsIn(r));
    return { row: r, col: Math.max(0, Math.min(cells - 1, col)) };
  };
  const row = clampRow(position.row);
  const cells = Math.max(1, shape.cellsIn(row));
  switch (event.key) {
    case 'ArrowDown':
      return { position: at(row + 1, wanted), wanted };
    case 'ArrowUp':
      return { position: at(row - 1, wanted), wanted };
    case 'PageDown':
      return { position: at(row + page, wanted), wanted };
    case 'PageUp':
      return { position: at(row - page, wanted), wanted };
    case 'ArrowRight': {
      const col = Math.min(cells - 1, position.col + 1);
      return { position: { row, col }, wanted: col };
    }
    case 'ArrowLeft': {
      const col = Math.max(0, position.col - 1);
      return { position: { row, col }, wanted: col };
    }
    case 'Home':
      return ctrl ? { position: at(0, 0), wanted: 0 } : { position: { row, col: 0 }, wanted: 0 };
    case 'End':
      if (ctrl) {
        const lastCells = Math.max(1, shape.cellsIn(last));
        return { position: { row: last, col: lastCells - 1 }, wanted: lastCells - 1 };
      }
      return { position: { row, col: cells - 1 }, wanted: cells - 1 };
    default:
      return null;
  }
}

/** Rows between two indexes, inclusive, for Shift+click range selection. */
export function rangeBetween<T>(items: readonly T[], from: number, to: number): T[] {
  const [a, b] = from <= to ? [from, to] : [to, from];
  return items.slice(Math.max(0, a), b + 1);
}

const GRID_KEYS = new Set([
  'ArrowUp',
  'ArrowDown',
  'ArrowLeft',
  'ArrowRight',
  'Home',
  'End',
  'PageUp',
  'PageDown',
  ' ',
  'Enter',
  'F2',
  'Delete',
  'Backspace',
]);

/**
 * Keys the grid handles itself, which must not reach the page's document listeners (the canvas deletes
 * selected nodes on Delete). Tool-window shortcuts (Ctrl+Shift+…, Shift+Esc) and F6 still pass.
 */
export function isGridKey(event: GridKey & { shiftKey?: boolean; altKey?: boolean }): boolean {
  const ctrl = !!(event.ctrlKey || event.metaKey);
  if (ctrl && event.shiftKey) return false;
  if (ctrl) return /^[ac]$/i.test(event.key);
  return GRID_KEYS.has(event.key);
}
